import { FORM_VALIDATOR_TYPES } from '../constants';
import { IExamEventForm, ISubjectForm } from '../interface/examDetails.interface';
import { hasSubjectStarted, hasSubjectFinished, isBeforeOrgNow } from '../utility/examStartTime';

// Exam Category Enum
export enum ExamCategory {
  TEST = 'TEST',
  QUIZ = 'QUIZ',
  HOMEWORK_DUE = 'HOMEWORK_DUE',
  PROJECT_DEADLINE = 'PROJECT_DEADLINE',
  SPECIAL_EVENT = 'SPECIAL_EVENT',
}

// Exam Status Enum
export enum ExamStatus {
  DRAFT = 'DRAFT',
  PUBLISHED = 'PUBLISHED',
  COMPLETED = 'COMPLETED',
  CANCELLED = 'CANCELLED',
}

/**
 * Exam delivery mode (AC: Online/Offline exam authoring). Mirrors the backend
 * `ExamDeliveryMode` enum values exactly so the value can be sent to
 * `CreateExamDto.deliveryMode` / `UpdateExamDto.deliveryMode` unchanged.
 * Offline exams behave exactly as before (schedule-only). Online exams
 * additionally require an exam-paper to be authored per subject via the
 * paper-builder phase (see ExamPaperBuilderPhase).
 */
export enum ExamMode {
  ONLINE = 'ONLINE',
  OFFLINE = 'OFFLINE',
}

// Resolved at call time (same `(t) => [...]` shape as EXAM_CATEGORY_OPTIONS /
// EXAM_STATUS_OPTIONS below) so the labels follow a live language switch instead
// of freezing in whatever language the module was first imported under.
export const EXAM_MODE_OPTIONS = (t: TFn) => [
  { label: t('exam.mode_offline', 'Offline'), value: ExamMode.OFFLINE, icon: 'pi pi-file' },
  { label: t('exam.mode_online', 'Online'), value: ExamMode.ONLINE, icon: 'pi pi-desktop' },
];

/**
 * Statuses from which an exam may be deleted (AC1). Positive allowlist: only
 * DRAFT, CANCELLED and COMPLETED are deletable — PUBLISHED (live / being marked;
 * there is no exam-level IN_PROGRESS) is blocked, and any future status is
 * non-deletable by default. Mirrors the backend EXAM_DELETABLE_STATUSES gate.
 */
export const DELETABLE_EXAM_STATUSES: ExamStatus[] = [
  ExamStatus.DRAFT,
  ExamStatus.CANCELLED,
  ExamStatus.COMPLETED,
];

export const isExamDeletable = (status?: ExamStatus | string): boolean =>
  !!status && DELETABLE_EXAM_STATUSES.includes(status as ExamStatus);

export enum ModalTypes {
  PUBLISH = 'PUBLISH',
  CANCEL = 'CANCEL',
  DRAFT = 'DRAFT',
}

// Exam Subject Status Enum
export enum ExamSubjectStatus {
  DRAFT = 'DRAFT',
  PENDING = 'PENDING',
  POSTPONED = 'POSTPONED',
  IN_PROGRESS = 'IN_PROGRESS',
  // CVA-4197 join-exam plan 0004: NEW, additive — NOT a rename of
  // IN_PROGRESS, which is untouched and still used for OFFLINE marking.
  // Set when a teacher starts marking an ONLINE subject; from then on it —
  // not IN_PROGRESS — is the "marking is active" state for that subject.
  // Mirrors the backend enum exactly (schema.prisma `ExamSubjectStatus`).
  GRADING = 'GRADING',
  COMPLETED = 'COMPLETED',
  CANCELLED = 'CANCELLED',
}

/**
 * Exam-subject statuses where marking is currently active — mirrors the
 * backend's `EXAM_SUBJECT_MARKING_ACTIVE_STATUSES`
 * (`src/common/constants/exam.constant.ts`). Both values are recognised
 * because the GRADING migration is purely additive with no data backfill:
 * a subject already IN_PROGRESS from before this deploy is genuinely still
 * "a teacher is marking this" too.
 */
export const EXAM_SUBJECT_MARKING_ACTIVE_STATUSES: ExamSubjectStatus[] = [
  ExamSubjectStatus.IN_PROGRESS,
  ExamSubjectStatus.GRADING,
];

/**
 * Exam-subject statuses whose row is locked in the schedule table — neither
 * editable nor removable.
 *
 * IN_PROGRESS/GRADING are actively being marked. COMPLETED and CANCELLED are
 * terminal: their `exam_marks` are real, and re-pointing such a row at a
 * different subject would silently turn one subject's marks into another's —
 * which is why the backend refuses that mutation too
 * (EXAM_SUBJECT_REPOINT_BLOCKED_STATUSES). Locking the whole row here is the
 * stricter, earlier expression of the same rule: the user never reaches the
 * state the backend would reject.
 */
export const LOCKED_SUBJECT_ROW_STATUSES: ExamSubjectStatus[] = [
  ...EXAM_SUBJECT_MARKING_ACTIVE_STATUSES,
  ExamSubjectStatus.COMPLETED,
  ExamSubjectStatus.CANCELLED,
];

export const isSubjectRowLocked = (status?: ExamSubjectStatus | string): boolean =>
  !!status && LOCKED_SUBJECT_ROW_STATUSES.includes(status as ExamSubjectStatus);

/**
 * Whether a subject's PAPER is frozen — the client mirror of the backend's
 * `ExamPaperUtils.getPaperAuthoringLock` (AC4).
 *
 * Two independent locks, matching the server exactly:
 *  - the row's status is already terminal/in-marking (`isSubjectRowLocked`);
 *  - or its scheduled start time has passed.
 *
 * The start-time lock CANNOT be read off a status: nothing transitions a
 * subject when its start time arrives, and `IN_PROGRESS` means "marking has
 * begun", which is after the sitting. POSTPONED is exempt because its old slot
 * has passed but the exam never ran — freezing it would make rescheduling
 * pointless.
 */
export const isSubjectPaperLocked = (
  status: ExamSubjectStatus | string | undefined,
  date: Date | string | null | undefined,
  startTime: string | null | undefined,
  /**
   * Whether this row already exists server-side. The START-TIME lock applies
   * only to stored subjects, mirroring the backend: its gate reads `start_time`
   * off the stored row, and on create no such row exists until rule R1 has
   * already guaranteed the slot is at least `EXAM_MIN_START_LEAD_MINUTES` in the
   * future — so the lock is unreachable there by construction.
   *
   * Without this, a teacher composing a new exam whose chosen slot lapsed while
   * they were still building would be shut out of their own unsaved draft, for
   * an exam no student can possibly be sitting because it does not exist yet.
   * The lapsed slot is still caught — by R1, at save, which is the rule that
   * owns it and which tells them to re-time rather than silently hiding work.
   */
  isPersisted = true,
): boolean => {
  if (isSubjectRowLocked(status)) return true;
  if (!isPersisted) return false;
  if (status === ExamSubjectStatus.POSTPONED) return false;
  return hasSubjectStarted(date, startTime);
};

/**
 * Statuses exempt from the elapsed-time locks below (CVA-4734) — mirrors the
 * backend's `getEditableExamSubjects` exactly: POSTPONED (its old slot passed
 * but the exam never ran, so freezing it would make rescheduling pointless)
 * and DRAFT (belongs to an exam never published — nothing was ever live for
 * a student to sit, so a stale time carries none of the "someone may be
 * mid-exam" risk these locks exist to close).
 */
const SUBJECT_TIME_LOCK_EXEMPT_STATUSES: ExamSubjectStatus[] = [
  ExamSubjectStatus.POSTPONED,
  ExamSubjectStatus.DRAFT,
];

/**
 * Whether a subject's own edit/delete row actions should be disabled once its
 * scheduled time has elapsed, in addition to `isSubjectRowLocked`'s DB-status
 * check (CVA-4734). Nothing auto-transitions a subject's status when its
 * sitting starts or ends — only a teacher's manual "start marking" action
 * does — so a subject that has already started or finished could otherwise
 * still show enabled edit/delete buttons while showing raw `PENDING`.
 */
export const isSubjectRowTimeLocked = (
  status: ExamSubjectStatus | string | undefined,
  date: Date | string | null | undefined,
  startTime: string | null | undefined,
): boolean => {
  if (isSubjectRowLocked(status)) return true;
  if (SUBJECT_TIME_LOCK_EXEMPT_STATUSES.includes(status as ExamSubjectStatus)) return false;
  return hasSubjectStarted(date, startTime);
};

/**
 * The status a subject should DISPLAY as, without touching the raw DB value
 * (CVA-4734). Only `PENDING` is ever overridden: `DRAFT` is exempt (same
 * reasoning as `SUBJECT_TIME_LOCK_EXEMPT_STATUSES`) and every other status is
 * already explicit/settled. A `PENDING` subject whose scheduled end has
 * passed displays as `COMPLETED`; one whose start has passed but not its end
 * displays as `IN_PROGRESS` — otherwise the raw status is returned unchanged.
 */
export const deriveDisplaySubjectStatus = (
  status: ExamSubjectStatus | string | undefined,
  date: Date | string | null | undefined,
  startTime: string | null | undefined,
  endTime: string | null | undefined,
): ExamSubjectStatus | string | undefined => {
  if (status !== ExamSubjectStatus.PENDING) return status;
  if (hasSubjectFinished(date, endTime)) return ExamSubjectStatus.COMPLETED;
  if (hasSubjectStarted(date, startTime)) return ExamSubjectStatus.IN_PROGRESS;
  return status;
};

export enum ExamGradeStatus {
  DRAFT = 'DRAFT',
  GRADED = 'GRADED',
  INITIAL = 'INITIAL',
  ABSENT = 'ABSENT',
  PENDING_RELEASE = 'PENDING_RELEASE',
}

/**
 * CVA-4197 join-exam — mirrors the backend's `EXAM_SUBJECT_ATTEMPT_STATUS`
 * (`exam.constant.ts`, backend). Derived server-side from `exam_attempt`, not
 * a stored column — `null` on the wire means N/A (an OFFLINE subject, where
 * the attempt concept doesn't exist at all).
 */
export enum ExamSubjectAttemptStatus {
  NOT_STARTED = 'NOT_STARTED',
  IN_PROGRESS = 'IN_PROGRESS',
  SUBMITTED = 'SUBMITTED',
}

/* ------------------------------------------------------------------ *
 * Marking (grading) flow                                              *
 * ------------------------------------------------------------------ */

/**
 * Marking is driven ENTIRELY by `exam_subjects.status` — never by the parent
 * exam's status. That is safe because the two are coupled server-side: a DRAFT
 * exam's subjects are created DRAFT, publishing is the only thing that produces
 * PENDING subjects, cancelling cascades to its subjects, and an exam can only
 * reach COMPLETED once every non-cancelled subject is already COMPLETED. So a
 * PENDING subject cannot exist under an unpublished exam, and no compensating
 * exam-status check is needed.
 *
 * Mirrors the backend guards in `exam-subject.service.ts`:
 *  - start-marking    requires PENDING (transitions to GRADING, plan 0004)
 *  - PATCH /marks     requires IN_PROGRESS or GRADING
 *  - complete-marking requires IN_PROGRESS or GRADING
 */
export const canStartMarking = (status?: ExamSubjectStatus | string): boolean =>
  status === ExamSubjectStatus.PENDING;

export const canEditMarks = (status?: ExamSubjectStatus | string): boolean =>
  !!status && EXAM_SUBJECT_MARKING_ACTIVE_STATUSES.includes(status as ExamSubjectStatus);

export const isMarkingComplete = (status?: ExamSubjectStatus | string): boolean =>
  status === ExamSubjectStatus.COMPLETED;

/**
 * A subject the marker can open at all. POSTPONED / CANCELLED / DRAFT subjects
 * are viewable but never editable, so they render a read-only notice.
 */
export const isMarkingReadOnly = (status?: ExamSubjectStatus | string): boolean =>
  !canEditMarks(status);

/** Score bounds — mirror `PatchExamSubjectMarkDto` (`@Min(0) @Max(100)`). */
export const EXAM_MARK_MIN_SCORE = 0;
export const EXAM_MARK_MAX_SCORE = 100;
/** Mirrors `@MaxLength(250)` on the same DTO. */
export const EXAM_MARK_REMARKS_MAX_LENGTH = 250;

/** Tabs on the teacher marking list. Index-based, matching `components/Tabs`. */
export enum MarkingTab {
  PENDING = 0,
  COMPLETED = 1,
}

export const MARKING_TAB_ITEMS = (t: TFn) => [
  { index: MarkingTab.PENDING, name: t('exam_marking.tab_pending', 'Pending') },
  { index: MarkingTab.COMPLETED, name: t('exam_marking.tab_completed', 'Completed') },
];

/**
 * Which tab an exam belongs to. Mirrors the mobile split
 * (`Teacher/ScoreCard/index.tsx`): terminal exams sit under Completed,
 * everything else under Pending. DRAFT exams never arrive — `GET /me?type=exam`
 * excludes them server-side.
 */
export const isExamTerminal = (status?: ExamStatus | string): boolean =>
  status === ExamStatus.COMPLETED || status === ExamStatus.CANCELLED;

/**
 * A subject row is only openable once its exam is live or finished. Mirrors
 * mobile's `isNavigable = isPublished || isCompleted`.
 */
export const isSubjectMarkingNavigable = (examStatus?: ExamStatus | string): boolean =>
  examStatus === ExamStatus.PUBLISHED || examStatus === ExamStatus.COMPLETED;

/** Status filter on the roster table. */
export enum MarkFilter {
  ALL = 'ALL',
  SCORED = 'SCORED',
  NOT_SCORED = 'NOT_SCORED',
  ABSENT = 'ABSENT',
  PENDING_RELEASE = 'PENDING_RELEASE',
}

export const MARK_FILTER_OPTIONS = (t: TFn) => [
  { label: t('exam_marking.filter_all', 'All'), value: MarkFilter.ALL },
  { label: t('exam_marking.filter_scored', 'Scored'), value: MarkFilter.SCORED },
  { label: t('exam_marking.filter_not_scored', 'Not scored'), value: MarkFilter.NOT_SCORED },
  {
    label: t('exam_marking.filter_pending_release', 'Pending release'),
    value: MarkFilter.PENDING_RELEASE,
  },
  { label: t('exam_marking.filter_absent', 'Absent'), value: MarkFilter.ABSENT },
];

/**
 * "Release Score" (PATCH /exam/:id/complete) is refused by the backend unless
 * every non-cancelled subject is COMPLETED. Both `GET /exam` and `GET /exam/:id`
 * run through the same formatter, so the list response already carries each
 * subject's status and this can be evaluated client-side — turning a guaranteed
 * 400 into an explainable disabled state.
 */
export const getUnreleasableSubjectNames = (
  examSubjects?: { subjectName?: string; status?: ExamSubjectStatus | string }[],
): string[] =>
  (examSubjects ?? [])
    .filter(
      (s) => s.status !== ExamSubjectStatus.COMPLETED && s.status !== ExamSubjectStatus.CANCELLED,
    )
    .map((s) => s.subjectName ?? '')
    .filter(Boolean);

export const canReleaseExamScore = (
  examStatus?: ExamStatus | string,
  examSubjects?: { subjectName?: string; status?: ExamSubjectStatus | string }[],
): boolean =>
  examStatus === ExamStatus.PUBLISHED && getUnreleasableSubjectNames(examSubjects).length === 0;

/**
 * Whether ANY of an exam's subjects has already started or finished
 * (CVA-4734) — the exam-level mirror of `ExamSubjectUtils.getEditableExamSubjects`
 * on the backend, used to fully disable "Edit Exam" for a published exam
 * instead of letting the user open an edit the backend will then reject.
 *
 * `examSubjects[].startTime` here is the already org-local-FORMATTED string
 * carried on `ExamSubject` (same convention as `IExamPaperView.startTime`,
 * CVA-4197 plan 0006/0009) — compared with `isBeforeOrgNow`, not
 * `hasSubjectStarted`, which expects a separate `HH:mm`-only field instead.
 */
export const hasAnySubjectStartedOrFinished = (
  examSubjects?: { status?: ExamSubjectStatus | string; startTime?: string }[],
): boolean =>
  (examSubjects ?? []).some((s) => {
    if (EXAM_SUBJECT_MARKING_ACTIVE_STATUSES.includes(s.status as ExamSubjectStatus)) return true;
    if (SUBJECT_TIME_LOCK_EXEMPT_STATUSES.includes(s.status as ExamSubjectStatus)) return false;
    return !!s.startTime && !isBeforeOrgNow(s.startTime);
  });

// Dropdown options for Exam Category (must be after enums)
type TFn = (key: string, def?: string) => string;
export const EXAM_CATEGORY_OPTIONS = (t: TFn) => [
  { label: t('exam.cat_test', 'Test'), value: ExamCategory.TEST },
  { label: t('exam.cat_quiz', 'Quiz'), value: ExamCategory.QUIZ },
  { label: t('exam.cat_homework_due', 'Homework Due'), value: ExamCategory.HOMEWORK_DUE },
  {
    label: t('exam.cat_project_deadline', 'Project Deadline'),
    value: ExamCategory.PROJECT_DEADLINE,
  },
  { label: t('exam.cat_special_event', 'Special Event'), value: ExamCategory.SPECIAL_EVENT },
];

// Dropdown options for Exam Status
export const EXAM_STATUS_OPTIONS = (t: TFn) => [
  { label: t('exam.status_draft', 'Draft'), value: ExamStatus.DRAFT },
  { label: t('exam.status_published', 'Published'), value: ExamStatus.PUBLISHED },
  { label: t('exam.status_completed', 'Completed'), value: ExamStatus.COMPLETED },
  { label: t('exam.status_cancelled', 'Cancelled'), value: ExamStatus.CANCELLED },
];

export const INIT_SUBJECT: ISubjectForm = {
  markingTeacher: {
    value: '',
    validator: FORM_VALIDATOR_TYPES.TEXT,
    isRequired: true,
    error: null,
    disable: false,
  },
  subjectName: {
    value: '',
    validator: FORM_VALIDATOR_TYPES.TEXT,
    isRequired: true,
    error: null,
    disable: false,
  },
  date: {
    value: null,
    validator: FORM_VALIDATOR_TYPES.DATE,
    isRequired: true,
    error: null,
    disable: false,
  },
  startTime: {
    value: '',
    validator: FORM_VALIDATOR_TYPES.TIME,
    isRequired: true,
    error: null,
    disable: false,
  },
  endTime: {
    value: '',
    validator: FORM_VALIDATOR_TYPES.TIME,
    isRequired: true,
    error: null,
    disable: false,
  },
  status: {
    value: ExamSubjectStatus.DRAFT,
    validator: null,
    isRequired: true,
    error: null,
    disable: false,
  },
};

export const INIT_FORM: IExamEventForm = {
  title: {
    value: '',
    validator: FORM_VALIDATOR_TYPES.TEXT,
    isRequired: true,
    error: null,
    disable: false,
  },
  category: {
    value: '',
    validator: FORM_VALIDATOR_TYPES.TEXT,
    isRequired: true,
    error: null,
    disable: false,
  },
  status: {
    value: ExamStatus.DRAFT,
    validator: null,
    isRequired: false,
    error: null,
    disable: true,
  },
  curriculum: {
    value: '',
    validator: FORM_VALIDATOR_TYPES.TEXT,
    isRequired: true,
    error: null,
    disable: false,
  },
  grade: {
    value: null,
    validator: FORM_VALIDATOR_TYPES.TEXT,
    isRequired: true,
    error: null,
    disable: false,
  },
  classroom: {
    value: null,
    validator: FORM_VALIDATOR_TYPES.TEXT,
    isRequired: true,
    error: null,
    disable: false,
  },
  teacher: {
    value: '',
    validator: FORM_VALIDATOR_TYPES.TEXT,
    isRequired: true,
    error: null,
    disable: false,
  },
  description: {
    value: '',
    validator: FORM_VALIDATOR_TYPES.TEXT,
    isRequired: false,
    error: null,
    disable: false,
  },
  mode: {
    value: ExamMode.OFFLINE,
    validator: null,
    isRequired: false,
    error: null,
    disable: false,
  },
  subjects: [JSON.parse(JSON.stringify(INIT_SUBJECT))],
};
